import { fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import type { BaseQueryFn, FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { RootState } from '.';

/**
 * Creates a base query with authentication and error handling for 401/token expiration
 * @param baseUrl The base URL for the API endpoints
 * @returns A configured base query function
 */
export const createBaseQueryWithAuth = (baseUrl: string): BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> => {
  const baseQuery = fetchBaseQuery({
    baseUrl,
    prepareHeaders: (headers, { getState }) => {
      const token = (getState() as RootState).auth.token;
      if (token) {
        headers.set('authorization', `Bearer ${token}`);
      }
      return headers;
    },
  });

  // Custom base query with error handling for 401/token expiration
  return async (args, api, extraOptions) => {
    const result = await baseQuery(args, api, extraOptions);

    // Check if the error is 401 or related to token expiration
    if (result.error && result.error.status === 401) {
      // Clear authentication data
      localStorage.removeItem('token');
      localStorage.removeItem('user');

      // Redirect to login page
      window.location.href = '/login';
    }

    return result;
  };
};
